import React from 'react';
import { CheckCircleOutlined, InfoOutlined } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Modal,
  TextField
} from '@mui/material';
import { PEDIGREE } from 'src/components/Constants';
import { CLINICAL_COLOURS } from 'src/theme/palette';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import { SnackContext, ThemeContext } from 'src/store/ContextStore';
import { CONSTANTS as c } from 'src/components/questionnaire/Constants';
import { OTHER_DIAGNOSIS_ID, getPersonAge } from 'src/util/util';
import { inkOn } from 'src/util/readableColour';
import { useLegendNodeDrop } from './legendDrag/useLegendNodeDrop';
import {
  sameMember,
  useLegendRecordWriter
} from './legendDrag/useLegendRecordWriter';
import { RECORD_KIND, recordKindOf } from './legendDrag/legendRecordPayload';

const Container = styled(Grid)({
  display: 'flex',
  gap: 15,
  flexWrap: 'wrap'
});

const DiseaseContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row'
});

const BoxHolder = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginRight: 10,

  fontSize: '1rem',
  '&[draggable="true"]': {
    cursor: 'grab'
  },
  '&[draggable="true"]:active': {
    cursor: 'grabbing'
  },
  '&.dragging': {
    opacity: 0.5
  }
});

const Item = styled('span')({
  display: 'inline-block',
  flexShrink: 0,
  width: 15,
  height: 15,
  borderRadius: 2,
  marginRight: 8
});

const ConfirmedText = styled('span')({
  fontStyle: 'normal',
  fontWeight: 'bold',
  fontSize: '0.75rem',
  color: 'black'
});

// A disease drop asks for the age at diagnosis first — except the pregnancy
// outcomes, which have none, and a legacy gene / HPO entry in this list, which
// is written as a genotype / phenotype.
const asksForAge = (item) =>
  recordKindOf(item) === RECORD_KIND.disease &&
  !c?.pregnanciesDiagnoses?.includes(item?.name);

export default function LegendMenu({
  diagnoses,
  id,
  labelsSet,
  setLabelsSet,
  showLegendTxt,
  dataset,
  setDataset,
  draggable,
  showCheckboxes = false,
  showMasterCheckbox = true,
  carrierPresentStates = {},
  showCarrierMarks = true,
  carrierStyle = 'border',
  // Drag-to-assign only (the canvas legend). See PedigreeChart.
  setPedigreeState,
  dropBlocker
}) {
  const t = useAutoTranslation();

  // The drop waiting on the age dialog: the person it resolved to and the
  // disease. Held TOGETHER and only here. The dialog used to write to
  // `nodeOverride || selectedNode || getNodeFromIndex(...)`, and selectedNode
  // was never cleared, so a later drop that resolved to nobody wrote to the
  // previous drop's person.
  const [pendingDrop, setPendingDrop] = React.useState(null);
  const [diagnosisAge, setDiagnosisAge] = React.useState('');
  const [diagnosisComment, setDiagnosisComment] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const { setSnack } = React.useContext(SnackContext);
  const { theme } = React.useContext(ThemeContext);

  const diseasesCases = React.useMemo(() => {
    const dg = {};
    (dataset || []).forEach((member) => {
      member.diagnoses?.forEach(
        (d) => (dg[d.id] = dg[d.id] === undefined ? 1 : dg[d.id] + 1)
      );
    });
    return dg;
  }, [dataset]);

  const handleChange = (e) => {
    setLabelsSet((prev) => ({
      ...prev,
      [e.target.name]: e.target.checked,
      diseasesMap: Object.keys(prev.diseasesMap).reduce((acc, key) => {
        acc[key] = e.target.checked;
        return acc;
      }, {})
    }));
  };

  const { checkTarget, write } = useLegendRecordWriter({
    setDataset,
    setPedigreeState,
    dropBlocker,
    enabled: Boolean(draggable)
  });

  const { draggedId, rowDragProps, releaseHighlight } = useLegendNodeDrop({
    enabled: Boolean(draggable),
    memberRecords: dataset,
    chipFor: (diagnosis) => {
      // The disease's own colour, with whichever of black or white ink reads on
      // it — white alone failed AA on 30 of the 46 palette colours.
      const background = diagnosis.color || '#000000';
      return {
        label: t(diagnosis.name),
        background,
        color: inkOn(background)
      };
    },
    onDrop: (diagnosis, target) => {
      // This legend CAN hold a legacy gene or HPO entry, so here — and only
      // here — the kind comes from the id.
      const kind = recordKindOf(diagnosis);
      const member = checkTarget(diagnosis, target, kind);
      if (!member) return false;
      if (!asksForAge(diagnosis)) {
        write({ member, item: diagnosis, kind });
        return false;
      }
      setPendingDrop({ target, item: diagnosis, kind });
      // Keep the person highlighted while the dialog asks about them.
      return true;
    }
  });

  const handleCloseModal = () => {
    setPendingDrop(null);
    setDiagnosisAge('');
    setDiagnosisComment('');
    releaseHighlight();
  };

  const handleSaveDiagnosis = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!pendingDrop || loading) return;
    const { target, item, kind } = pendingDrop;

    // "Other Disease/Comment" says nothing on its own: the comment IS the
    // record. It used to be copied from whoever the legend entry came from.
    if (item.id === OTHER_DIAGNOSIS_ID && !diagnosisComment.trim()) {
      setSnack({
        status: true,
        msg: t('Please describe the disease in the comment.'),
        severity: 'error'
      });
      return;
    }

    // Ask again: the dialog can stay open while an age is looked up, and the
    // person or the pedigree can be locked by someone else in the meantime.
    // Against the person's row as it is NOW, not as it was at the drop.
    const live = (dataset || []).find((r) => sameMember(r, target.member));
    const member = checkTarget(
      item,
      { ...target, member: live || target.member },
      kind
    );
    if (!member) {
      handleCloseModal();
      return;
    }

    const personAge = getPersonAge(member);
    if (personAge !== null && Number(diagnosisAge) > personAge) {
      setSnack({
        status: true,
        msg: t(
          "Age at diagnosis cannot be greater than the person's age ({{personAge}}).",
          { personAge }
        ),
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      await write({
        member,
        item,
        kind,
        age: diagnosisAge,
        comment: diagnosisComment
      });
    } finally {
      setLoading(false);
      handleCloseModal();
    }
  };

  const currentDiagnosis = pendingDrop?.item;

  const labels = {
    diseases: 'Diseases'
  };

  const isClinicallyConfirmed = diagnoses.some(
    (diagnose) => diagnose.clinicallyConfirmed
  );

  const handleDiseaseToggle = (id) => {
    setLabelsSet((prev) => ({
      ...prev,
      diseasesMap: {
        ...prev.diseasesMap,
        [id]: !prev.diseasesMap[id]
      }
    }));
  };

  return (
    <>
      <Grid>
        {labelsSet && showCheckboxes && showMasterCheckbox && (
          <FormGroup>
            {Object.keys(PEDIGREE.legendLabelFilters).map((label) => {
              return (
                <FormControlLabel
                  key={label}
                  control={
                    <Checkbox
                      checked={labelsSet[label]}
                      onChange={handleChange}
                      size="small"
                      name={label}
                    />
                  }
                  label={t(labels[label])}
                />
              );
            })}
          </FormGroup>
        )}
        {(!labelsSet || labelsSet?.diseases) && (
          <Container container id={id} style={{ gap: showCheckboxes ? 4 : 15 }}>
            {diagnoses
              .filter(
                (el) =>
                  showCheckboxes ||
                  !labelsSet?.diseasesMap ||
                  labelsSet.diseasesMap[el.id] !== false
              )
              .map((el, i) => {
                const isDragging = draggedId === el.id;
                return (
                  <DiseaseContainer key={i}>
                    {labelsSet && showCheckboxes && (
                      <Checkbox
                        checked={labelsSet?.diseasesMap?.[el.id]}
                        onChange={() => handleDiseaseToggle(el.id)}
                        size="small"
                      />
                    )}

                    <BoxHolder
                      {...rowDragProps(el)}
                      className={isDragging ? 'dragging' : ''}
                      title={
                        draggable
                          ? t('Drag {{name}} to assign to a family member', {
                              name: el.name
                            })
                          : undefined
                      }
                    >
                      <Item
                        style={{
                          backgroundColor: el.color ?? 'black'
                        }}
                      />
                      <span>
                        {t(el.name)}
                        {el.id ? (
                          <span
                            style={{ fontSize: '0.85rem', color: '#757575' }}
                          >
                            {` (${el.id})`}
                          </span>
                        ) : (
                          ''
                        )}
                        <Chip
                          label={t('{{value}} cases', {
                            value: diseasesCases[el.id] || 0
                          })}
                          size={'small'}
                          sx={{
                            fontStyle: 'italic',
                            fontSize: 11,
                            mx: 1,
                            height: 1
                          }}
                        />
                      </span>
                    </BoxHolder>
                  </DiseaseContainer>
                );
              })}
            {showCarrierMarks &&
              diagnoses.some(
                (el) =>
                  (!labelsSet?.diseasesMap ||
                    labelsSet.diseasesMap[el.id] !== false) &&
                  carrierPresentStates?.[el.id]?.has?.('carrier')
              ) && (
                <div style={{ width: '100%', marginTop: 10 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      marginBottom: 4
                    }}
                  >
                    <ConfirmedText style={{ color: theme?.header }}>
                      {t('Carriers')}
                    </ConfirmedText>
                    <Tooltip
                      title={t(
                        carrierStyle === 'verticalLine'
                          ? "Carriers are shown by a vertical line through the symbol in the disease's colour, following Bennett et al. 2022 pedigree nomenclature. This style is used when a single disease is filtered on the pedigree."
                          : "Carriers are shown as a bold coloured outline on the relevant disease segment, in the disease's colour. This is a TrakGene-specific convention that extends standard pedigree notation to multi-disease pedigrees."
                      )}
                      enterTouchDelay={0}
                      leaveTouchDelay={4000}
                      arrow
                    >
                      <InfoOutlined
                        sx={{ fontSize: 15, opacity: 0.7, cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </div>
                  {diagnoses
                    .filter(
                      (el) =>
                        (!labelsSet?.diseasesMap ||
                          labelsSet.diseasesMap[el.id] !== false) &&
                        carrierPresentStates?.[el.id]?.has?.('carrier')
                    )
                    .map((el, i) => (
                      <DiseaseContainer
                        key={`carrier-${el.id ?? i}`}
                        style={{ alignItems: 'center', marginBottom: 8 }}
                      >
                        {carrierStyle === 'verticalLine' ? (
                          <Item
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid #bdbdbd',
                              position: 'relative',
                              overflow: 'visible'
                            }}
                          >
                            <span
                              style={{
                                position: 'absolute',
                                left: '50%',
                                top: -4,
                                bottom: 0,
                                width: 2,
                                transform: 'translateX(-50%)',
                                backgroundColor: el.color ?? 'black'
                              }}
                            />
                          </Item>
                        ) : (
                          <Item
                            style={{
                              backgroundColor: 'transparent',
                              border: `2.5px solid ${el.color ?? 'black'}`
                            }}
                          />
                        )}
                        <span style={{ fontSize: '0.85rem' }}>
                          {t(el.name)}
                          {el.id ? (
                            <span style={{ color: '#757575' }}>
                              {` (${el.id})`}
                            </span>
                          ) : (
                            ''
                          )}
                        </span>
                      </DiseaseContainer>
                    ))}
                  <div
                    style={{
                      fontSize: 11,
                      fontStyle: 'italic',
                      opacity: 0.8,
                      marginTop: 4
                    }}
                  >
                    {t(
                      "Tested carriers have a variant label; other carriers don't."
                    )}
                  </div>
                </div>
              )}
            {showLegendTxt && isClinicallyConfirmed && !showCheckboxes && (
              <div style={{ width: '100%' }}>
                <hr
                  style={{
                    border: 'none',
                    borderTop: '1px solid currentColor',
                    opacity: 0.4,
                    margin: '6px 0'
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {labelsSet && showCheckboxes && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: 42,
                        flexShrink: 0
                      }}
                    />
                  )}
                  <BoxHolder style={{ cursor: 'default' }}>
                    <svg
                      width="15"
                      height="15"
                      style={{ flexShrink: 0, marginRight: 8 }}
                      aria-hidden="true"
                    >
                      <defs>
                        <pattern
                          id="legend-dx-confirmed-stripe"
                          x="0"
                          y="0"
                          width="8"
                          height="8"
                          patternUnits="userSpaceOnUse"
                          patternTransform="rotate(45)"
                        >
                          <rect
                            x="0"
                            y="0"
                            width="4"
                            height="8"
                            fill="white"
                            fillOpacity="0.55"
                          />
                        </pattern>
                      </defs>
                      <rect
                        x="0"
                        y="0"
                        width="15"
                        height="15"
                        rx="2"
                        fill="#9e9e9e"
                      />
                      <rect
                        x="0"
                        y="0"
                        width="15"
                        height="15"
                        rx="2"
                        fill="url(#legend-dx-confirmed-stripe)"
                      />
                    </svg>
                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                      {t('Clinically confirmed diagnosis')}
                    </span>
                    <CheckCircleOutlined
                      sx={{
                        fontSize: 16,
                        color: CLINICAL_COLOURS.clinicallyConfirmed,
                        ml: 0.5
                      }}
                    />
                  </BoxHolder>
                </div>
              </div>
            )}
          </Container>
        )}
      </Grid>
      <Modal open={Boolean(pendingDrop)} onClose={handleCloseModal}>
        <CustomDialog
          maxWidth={'sm'}
          // An element, not a string: CustomDialog translates a string title,
          // and this one carries the disease's name, which is never a key.
          title={
            <>
              {t('Age {{name}} Diagnosed', {
                name: currentDiagnosis?.name || ''
              })}
            </>
          }
          open={Boolean(pendingDrop)}
          handleClose={handleCloseModal}
          minHeight={'auto'}
          onClose={true}
        >
          <form className="user-form">
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label={t('Age at {{value}} diagnosis', {
                    value: currentDiagnosis?.name || ''
                  })}
                  onChange={(e) => setDiagnosisAge(e.target.value)}
                  name={currentDiagnosis?.name || ''}
                  value={diagnosisAge}
                  placeholder={t('Enter age')}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  label={t('Diagnosis Comment')}
                  required={currentDiagnosis?.id === OTHER_DIAGNOSIS_ID}
                  onChange={(e) => setDiagnosisComment(e.target.value)}
                  value={diagnosisComment}
                  placeholder={t('Enter additional details or comments...')}
                />
              </Grid>
              <ModalButtons
                hideContinueAdding={true}
                handleClose={handleCloseModal}
                onClick={handleSaveDiagnosis}
                addLabel={`Save`}
                loading={loading}
              />
            </Grid>
          </form>
        </CustomDialog>
      </Modal>
    </>
  );
}
